import Controller, { OnAfterFetch, OnBeforeCancel, OnBeforeSubmit, OnMount } from '@philips-internal/oswald-ui-core/lib/client';
import BaseController from '../../BaseController';
import {DevicesAnotherActionAliases, FragmentNames} from '../../../codegen';
import {Inject} from '@odinjs/odin';
import { AutocompleteAPI, DynamicFieldsAPI, FieldAPI, FieldsetAPI, FormAPI, Metadata, ToastService } from '@philips-internal/oswald-ui-components';
import moment from 'moment';
import { ISO_FULL_DATE_FORMAT } from '../../../constants/date-utils';
import { HttpService, } from '@philips-internal/oswald-ui-core/lib/services';
import {Endpoint} from '../../../constants/endpoints';
import { applicationContext, foundationContext } from '@philips-internal/oswald-ui-foundation';
import { Placement, ToastLevel, ToastType } from '@philips-internal/oswald-ui-constants';
import { HealthProfessionalUser } from '../../../app/authentication';
import { ActionType, DevicesViewerType } from '../commons/register-device';
import { Value } from '@philips-internal/oswald-ui-core';
import { AbortableSubmitEvent, FetchEvent } from '@philips-internal/oswald-ui-core/lib/component/types';
import { Validator } from '@philips-internal/oswald-ui-core/lib/services/model-service';
import { DevicesDynamicForm } from '../commons/devices-dynamic-form';

enum DeviceType {
  DEVICE_TYPE = 'O',
}

@Controller({
  component: DevicesAnotherActionAliases.LAYOUT_DEVICES_ANOTHER_ACTION,
  fragment: FragmentNames.DEVICES_ANOTHER_ACTION
})
class DevicesAnotherActionController extends BaseController {

  @Inject(DevicesAnotherActionAliases.FORM_DEVICES_ANOTHER_ACTION_VIEWER)
  formDeviceAnotherActionAPI!: FormAPI;

  @Inject(DevicesAnotherActionAliases.FIELDSET_DYNAMIC_FIELDS)
  fieldsetDynamicAPI!: FieldsetAPI;

  @Inject(DevicesAnotherActionAliases.DYNAMIC_FIELDS)
  dynamicFieldAPI!: DynamicFieldsAPI;

  @Inject(DevicesAnotherActionAliases.FIELD_ANOTHER_ACTION)
  fieldAnotherActionAPI!: FieldAPI;

  @Inject(DevicesAnotherActionAliases.AUTOC_ANOTHER_ACTION)
  autocompleteAnotherActionAPI!: AutocompleteAPI;

  private showToast(message: string, level: ToastLevel): void {
    ToastService.open({
      dismissible: true,
      highlight: false,
      id: 'device-remove-validation',
      level,
      message,
      placement: Placement.BOTTOM,
      showIcon: true,
      timeout: 5000,
      type: ToastType.BANNER,
    })
  }

  private returnDevicesList(): void {
    this.historyService.back();
    applicationContext.popNavigationItem();
  }

  private submitAnotherAction(body: Record<string, Value>): void {
    this.formDeviceAnotherActionAPI.setLoading(true);
    HttpService.post(Endpoint.DEVICES_REGISTER, undefined, {body})
      .then(() => {
        this.showToast('patient-evolution.release-evolution', ToastLevel.SUCCESS);
        this.returnDevicesList();
      })
      .catch((err) => {
        this.showToast(err?.body.errorMessage, ToastLevel.ERROR);
      }).finally(() => this.formDeviceAnotherActionAPI.setLoading(false));
  }

  @OnMount(DevicesAnotherActionAliases.DYNAMIC_FIELDS)
  handleMountDynamicFields(): void {
    const dynamicFields = this.context.get('dynamicFields') as Metadata;
    this.dynamicFieldAPI.setMetadata(dynamicFields);
  }

  private handleDynamicFields(): void {
    const {deviceSequence} = this.historyService.getCurrentRouteParams();
    const deviceType = DeviceType.DEVICE_TYPE;

    DevicesDynamicForm.handleDynamicFields(this.fieldsetDynamicAPI, deviceSequence, deviceType, this.context);
  }

  @OnBeforeSubmit({ target: DevicesAnotherActionAliases.FORM_DEVICES_ANOTHER_ACTION_VIEWER })
  handleBeforeSubmitForm(event: AbortableSubmitEvent): void {
    event.abort();
    const formInfos = DevicesDynamicForm.createFormPattern(event);
    const formData = this.formDeviceAnotherActionAPI.getRecord();
    const {patientEncounterDeviceId, encounterId} = this.historyService.getCurrentRouteParams();
    const devSeq = this.context.get('deviceSequence');
    const user = foundationContext.getUser() as unknown as HealthProfessionalUser;
    const formatDateTime = moment(formData.date as string).utc().format(ISO_FULL_DATE_FORMAT);
    const observation = formData.description as string;
    const anotherAction = formData.anotherAction as ActionType;
    const body = {
      deviceAttributes: formInfos,
      deviceSequence: devSeq,
      encounterId: encounterId as number,
      eventDescription: observation,
      optionStatus: DeviceType.DEVICE_TYPE,
      otherActionSequence: anotherAction?.internalProcedureSequence,
      professionalCode: user.physicalPersonId,
      registerDate: formatDateTime,
      registerSequence: patientEncounterDeviceId as number,
      releasedStatus:'S',
      removeStatusDischarge: 'N',
    };
    this.submitAnotherAction(body);
  }

  @OnMount(DevicesAnotherActionAliases.FORM_DEVICES_ANOTHER_ACTION_VIEWER)
  onMount(): void {
    const {patientEncounterDeviceId, patientId, encounterId} = this.historyService.getCurrentRouteParams();
    const date = moment().utc().format(ISO_FULL_DATE_FORMAT);
    this.formDeviceAnotherActionAPI.setLoading(true);
    HttpService.get(Endpoint.GET_DEVICES_VIEWER, {
      encounterId,
      patientEncounterDeviceId,
      patientId,
    }).then((response) => {
      const res = response.body as DevicesViewerType;
      const deviceDescription = res.deviceDescription;
      const deviceSequence = res.deviceSequence;
      this.context.set({
        deviceSequence: deviceSequence,
        encounterId: encounterId,
        patientId: patientId,
      });
      this.formDeviceAnotherActionAPI.setData({date, deviceDescription});
      this.handleDynamicFields();
    }).finally(() => this.formDeviceAnotherActionAPI.setLoading(false));
  }

  @OnBeforeCancel({ target: DevicesAnotherActionAliases.FORM_DEVICES_ANOTHER_ACTION_VIEWER})
  handleCancelForm(event: AbortableSubmitEvent): void {
    event.abort();
    this.returnDevicesList();
  }

  @OnAfterFetch({ target: DevicesAnotherActionAliases.FETCH_ANOTHER_ACTION })
  handleAfterFetch(event: FetchEvent): void {
    const validator: Validator = { name: 'required', options: {}};
    const resArray = event.data as Record<string, string>[];
    const hasAnotherActions = resArray.length > 0
    this.fieldAnotherActionAPI.addValidator(validator);
    this.fieldAnotherActionAPI.validate();
    this.autocompleteAnotherActionAPI.setEnabled(hasAnotherActions)
  }

}

export default DevicesAnotherActionController;
